import { Component, OnInit } from '@angular/core';
import { DatePipe } from '@angular/common';
import { Router } from '@angular/router';
import { CalendarioService } from '@services/calendario/calendario.service';
import { EventoCalendario } from '@interfaces/calendario/calendario-interface';
import { animate, style, transition, trigger } from '@angular/animations';
import { MatDialog } from '@angular/material/dialog';
import Swal from 'sweetalert2';
import { GuiMsjService } from '@services/shared/gui-msj.service';
import { ChangeDetectorRef } from '@angular/core';

@Component({
  selector: 'app-calendario',
  templateUrl: './calendario.component.html',
  styleUrls: ['./calendario.component.scss'],
  animations: [
    trigger('fadeInOut', [
      transition(':increment, :decrement', [
        style({ opacity: 0, transform: 'translateY(-5px)' }),
        animate('300ms ease-out', style({ opacity: 1, transform: 'translateY(0)' }))
      ])
    ])
  ]
})
export class CalendarioComponent implements OnInit {

  readonly diasSemana = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];
  semanas: Date[][] = [];
  currentDate = new Date();
  mostrarSelectorMeses = false;
  mostrarSelectorAnios = false;
  mesesDelAnio = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 
    'Mayo', 'Junio', 'Julio', 'Agosto',
    'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
  ];
  rangoAnios: number[] = [];
  eventos: EventoCalendario[] = [];
  animationTrigger = 0;

  eventosDelDia: EventoCalendario[] = [];
  expandedEventos = new Map<string, boolean>();
  diaSeleccionado: Date | null = null;
  
  isDeleting: boolean = false;
  isLoading: boolean = false;

  // OPTIMIZACIONES: Cache y estructuras de datos eficientes
  private eventosCache = new Map<string, EventoCalendario[]>();
  private currentMonthKey: string = '';
  private eventosPorDiaMap = new Map<string, EventoCalendario[]>();

  constructor(
    private calendarioService: CalendarioService,
    private router: Router,
    private datePipe: DatePipe,
    private dialog: MatDialog,
    private guiMsjService: GuiMsjService,
    private changeDetectorRef: ChangeDetectorRef
  ) {
    this.generarRangoAnios();
  }

  ngOnInit(): void {
    this.generarEstructuraMensual();
    this.cargarEventos();
  }

  esDiaSeleccionado(dia: Date): boolean {
    return this.diaSeleccionado ? 
      dia.toDateString() === this.diaSeleccionado.toDateString() : 
      false;
  }

  private generarRangoAnios(): void {
    const anioActual = new Date().getFullYear();
    this.rangoAnios = Array.from({ length: 6 }, (_, i) => anioActual - 2 + i);
  }

  async cambiarMes(delta: number): Promise<void> {
    this.animationTrigger++;
    this.currentDate = new Date(
      this.currentDate.getFullYear(),
      this.currentDate.getMonth() + delta,
      1
    );
    this.generarEstructuraMensual();
    await this.cargarEventos();
  }

  seleccionarMes(mesIndex: number): void {
    this.currentDate = new Date(
      this.currentDate.getFullYear(),
      mesIndex,
      1
    );
    this.mostrarSelectorMeses = false;
    this.generarEstructuraMensual();
    this.cargarEventos();
  }

  seleccionarAnio(anio: number): void {
    this.currentDate = new Date(
      anio,
      this.currentDate.getMonth(),
      1
    );
    this.mostrarSelectorAnios = false;
    this.generarEstructuraMensual();
    this.cargarEventos();
  }

  onDiaClick(dia: Date): void {
    this.diaSeleccionado = dia;
    this.actualizarEventosDelDia();
  }

  toggleExpandida(eventoId: string): void {
    this.expandedEventos.set(eventoId, !this.expandedEventos.get(eventoId));
  }

  private async cargarEventos(): Promise<void> {
    const monthKey = `${this.currentDate.getFullYear()}-${this.currentDate.getMonth()}`;
    this.currentMonthKey = monthKey;

    // Verificar cache primero - si hay cache, no mostrar loading
    if (this.eventosCache.has(monthKey)) {
      this.eventos = this.eventosCache.get(monthKey)!;
      this.actualizarEventosPorDiaMap();
      this.actualizarEventosDelDia();
      return;
    }

    // Si no hay cache, mostrar loading y cargar desde el backend
    this.isLoading = true;
    
    try {
      this.eventos = await this.calendarioService.obtenerEventos();
      this.eventosCache.set(monthKey, this.eventos);
      this.actualizarEventosPorDiaMap();
      this.actualizarEventosDelDia();
    } catch (error) {
      console.error('Error cargando eventos:', error);
    } finally {
      this.isLoading = false;
    }
  }

  private actualizarEventosDelDia(): void {
    if (!this.diaSeleccionado) return;

    const claveDiaSeleccionado = this.obtenerClaveDia(this.diaSeleccionado);
    this.eventosDelDia = this.eventosPorDiaMap.get(claveDiaSeleccionado) || [];

    this.expandedEventos.clear();
    this.eventosDelDia.forEach(evento => {
      this.expandedEventos.set(evento._id!, false);
    });
  }

  // Pre-calcular eventos por día una sola vez
  private actualizarEventosPorDiaMap(): void {
    this.eventosPorDiaMap.clear();
    
    this.eventos.forEach(evento => {
      const fechaEvento = new Date(evento.fechaInicio);
      const claveDia = this.obtenerClaveDia(fechaEvento);
      
      if (!this.eventosPorDiaMap.has(claveDia)) {
        this.eventosPorDiaMap.set(claveDia, []);
      }
      this.eventosPorDiaMap.get(claveDia)!.push(evento);
    });
  }

  private obtenerClaveDia(fecha: Date): string {
    return `${fecha.getFullYear()}-${fecha.getMonth() + 1}-${fecha.getDate()}`;
  }

  private esMismoDia(fecha1: Date, fecha2: Date): boolean {
    return fecha1.getFullYear() === fecha2.getFullYear() &&
           fecha1.getMonth() === fecha2.getMonth() &&
           fecha1.getDate() === fecha2.getDate();
  }

  private generarEstructuraMensual(): void {
    const primerDiaMes = new Date(
      this.currentDate.getFullYear(),
      this.currentDate.getMonth(),
      1
    );
    
    const fechaInicio = new Date(primerDiaMes);
    fechaInicio.setDate(fechaInicio.getDate() - (fechaInicio.getDay() === 0 ? 6 : fechaInicio.getDay() - 1));

    this.semanas = [];
    for (let i = 0; i < 6; i++) {
      const semana: Date[] = [];
      for (let j = 0; j < 7; j++) {
        semana.push(new Date(fechaInicio));
        fechaInicio.setDate(fechaInicio.getDate() + 1);
      }
      this.semanas.push(semana);
    }
  }

  esHoy(fecha: Date): boolean {
    return fecha.toDateString() === new Date().toDateString();
  }

  perteneceAlMesActual(fecha: Date): boolean {
    return fecha.getMonth() === this.currentDate.getMonth();
  }

  obtenerEventosDelDia(fecha: Date): EventoCalendario[] {
    const claveDia = this.obtenerClaveDia(fecha);
    return this.eventosPorDiaMap.get(claveDia)?.slice(0, 3) || [];
  }

  trackByWeek(index: number): number {
    return index + this.animationTrigger;
  }

  trackByDay(index: number, dia: Date): string {
    return dia.toISOString();
  }

  async eliminarEvento(id: string): Promise<void> {
    const confirmado = await Swal.fire({
      title: '¿Eliminar evento?',
      text: "Esta acción no se puede deshacer",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#124E91',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Sí, eliminar'
    });

    if (!confirmado.isConfirmed) {
      return;
    }

    this.isDeleting = true;
    
    try {
      const exito = await this.calendarioService.deleteEvento(id);
      
      if (!exito) {
        Swal.fire('Error', 'No se pudo eliminar el evento. Intente de nuevo.', 'error');
        return;
      }

      // Eliminación exitosa
      await this.guiMsjService.msjFormSubmit('Evento eliminado correctamente');
      
      this.eventosCache.clear();
      this.eventosPorDiaMap.clear();
      this.generarEstructuraMensual();
      await this.cargarEventos();
      if (this.diaSeleccionado) {
        this.actualizarEventosDelDia();
      }
      this.changeDetectorRef.detectChanges();
      
    } catch (error) {
      console.error('Error en el proceso de eliminación:', error);
      Swal.fire('Error', 'Ocurrió un error inesperado al eliminar el evento.', 'error');
    } finally {
      this.isDeleting = false;
    }
  }

  editarEvento(id: string): void {
    this.router.navigate(['/eventos-calendario/editar', id]);
  }

}